import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * AdvanceRefundDialog
 * -------------------
 * Returns a portion of a customer's advance back as cash. Source account is
 * debited; customer advance_balance is decremented; a REF-XXXXX voucher is
 * created (auto-approved). The refund is capped at the customer's currently
 * available advance — the form blocks larger amounts before submit and the
 * backend re-validates.
 *
 * Props:
 *  - open / onOpenChange
 *  - customer: { id, name, advance_balance, phone? } — pre-selected
 *  - onSaved(): called after a successful refund so the parent can refetch
 */
export default function AdvanceRefundDialog({ open, onOpenChange, customer, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form values when dialog opens.
  useEffect(() => {
    if (!open) return;
    setAmount('');
    setDate(todayISO());
    setDescription('');
  }, [open]);

  // Load money accounts when the dialog opens and preselect cash. Same picker
  // rule as the rest of the app: cash / bank / wallet / creditor always;
  // credit_card only when balance ≤ 0.
  useEffect(() => {
    if (!open) return;
    api.get('/finance/accounts').then(r => {
      const all = (r.data || []).filter(a => {
        if (['cash', 'bank', 'wallet', 'creditor'].includes(a.type)) return true;
        if (a.type === 'credit_card') return (a.balance || 0) <= 0;
        return false;
      });
      setAccounts(all);
      const cash = all.find(a => a.type === 'cash');
      setAccount(cash ? cash.id : (all[0]?.id || ''));
    }).catch(() => {});
  }, [open]);

  const advance = Number(customer?.advance_balance || 0);
  const amt = Number(amount || 0);
  const isInvalid = !customer?.id || !account || amt <= 0 || amt > advance;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (isInvalid || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post('/finance/advance-refund', {
        customer_id: customer.id,
        amount: amt,
        account,
        date,
        description: description.trim(),
      });
      toast.success(`Refund ${r.data.voucher_number} recorded — ${fmt(amt)} to ${customer.name}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record refund');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="advance-refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Refund Advance</DialogTitle>
          <DialogDescription>
            Return part of {customer?.name || 'customer'}&apos;s advance from a money account.
            Available advance: <strong className="text-[#3B82F6]">{fmt(advance)}</strong>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label>
            <Input
              type="number"
              min="1"
              step="any"
              max={advance}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder={`Up to ${fmt(advance)}`}
              required
              data-testid="refund-amount-input"
              className="rounded-lg mt-1"
            />
            {amt > advance && (
              <p className="text-[11px] text-[#EF4444] mt-1">Exceeds available advance of {fmt(advance)}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Refund From *</Label>
              <Select value={account} onValueChange={setAccount}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="refund-account-select"><SelectValue placeholder="Pick account..." /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center justify-between gap-2 w-full">
                        <span>{a.name} <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">({(a.type || '').replace('_', ' ')})</span></span>
                        <span className="text-xs font-mono text-[#10B981]">{fmt(a.balance)}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-lg mt-1" data-testid="refund-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Note</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional reason / reference"
              rows={2}
              className="rounded-lg mt-1 text-sm"
              data-testid="refund-note-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg" data-testid="refund-cancel-btn">Cancel</Button>
            <Button
              type="submit"
              disabled={isInvalid || submitting}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-lg"
              data-testid="refund-submit-btn"
            >
              {submitting ? 'Saving…' : `Refund ${fmt(amt || 0)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
